/* Limits and read-only helpers shared by every poll screen. */

import moment from 'moment';

import { MSG_PROCESSING_STATUS_ID, POLL_MESSAGE_TYPE } from '../../Helpers/Chat/Constant';
import { isLocalUser } from '../../Helpers/Chat/User';

export { POLL_MESSAGE_TYPE };
export const POLL_QUESTION_MAX_LENGTH = 255;
export const POLL_OPTION_MAX_LENGTH = 100;
export const POLL_MIN_OPTIONS = 2;
export const POLL_MAX_OPTIONS = 12;
export const POLL_MIN_OPEN_MS = 60 * 1000;
export const POLL_DEFAULT_DEADLINE_MS = 60 * 60 * 1000;
export const POLL_CARD_OPTION_COUNT = 3;
export const POLL_VOTERS_PAGE_SIZE = 50;
export const POLL_BATCH_MAX = 50;

export const POLL_STATUS = { OPEN: 'open', CLOSED: 'closed' };

export const POLL_EVENT = {
    CREATED: 'created',
    VOTED: 'voted',
    VOTE_CHANGED: 'vote_changed',
    OPTION_ADDED: 'option_added',
    CLOSED: 'closed',
    RESHARED: 'reshared',
};

export const POLL_CLOSE_REASON = { MANUAL: 'manual', DEADLINE: 'deadline' };

// Refusals the client branches on; the rest show the generic error.
export const POLL_ERROR = {
    DEADLINE_TOO_SOON: 'DEADLINE_TOO_SOON',
    CLOSES_BEFORE_SCHEDULE: 'POLL_CLOSES_BEFORE_SCHEDULE',
    SCHEDULE_TIME_PAST: 'SCHEDULE_TIME_PAST',
    POLL_CLOSED: 'POLL_CLOSED',
    OPTION_LIMIT_REACHED: 'OPTION_LIMIT_REACHED',
};

// Activity id of a "created" label built on the client rather than read from history.
export const LOCAL_CREATED_PREFIX = 'created-';

// Times above this are microseconds; epoch milliseconds stay below it for millennia.
const MICROSECONDS_THRESHOLD = 1e14;
const DATE_TIME_FORMAT = 'YYYY/MM/DD HH:mm:ss';
// An unmoved poll message was sent right after its poll was stored.
const FIRST_POSITION_WINDOW_MS = 5000;

/**
 * Tells whether a chat message carries a poll.
 *
 * @param {object} message Message from the conversation history.
 * @returns {boolean} True for a poll message.
 */
export const isPollMessage = message => message?.msgBody?.message_type === POLL_MESSAGE_TYPE;

/**
 * Whether the thread should jump to a poll message as to the user's own new message: only a
 * Pending poll the user just created. A poll moved by someone's vote must not pull them down.
 *
 * @param {object} message Poll message.
 * @returns {boolean} True for the user's own Pending poll.
 */
export const isOwnPendingPoll = message =>
    message.msgStatus === MSG_PROCESSING_STATUS_ID && isLocalUser(message.publisherId);

/**
 * Oldest loaded message to page older history from. A poll can move to the bottom at any
 * time and the server resolves the cursor to its current row, so it is never the cursor.
 *
 * @param {object} messages Loaded messages keyed by msgId, oldest first.
 * @returns {string} msgId of the oldest message that is not a poll, else the oldest.
 */
export const getPagingAnchorId = (messages = {}) => {
    const messageIds = Object.keys(messages);
    return messageIds.find(msgId => !isPollMessage(messages[msgId])) || messageIds[0];
};

/**
 * Brings a server time to epoch milliseconds; history may carry microseconds.
 *
 * @param {number|string} time Epoch time in milliseconds or microseconds.
 * @returns {number} Epoch milliseconds, 0 when unusable.
 */
export const toMillis = time => {
    const value = Number(time);
    if (!Number.isFinite(value) || value <= 0) return 0;
    return value > MICROSECONDS_THRESHOLD ? Math.floor(value / 1000) : value;
};

/**
 * Whether a poll message has never moved since it was sent.
 *
 * @param {object} message Poll message.
 * @param {object} poll Server poll, when loaded.
 * @returns {boolean} True at the first position.
 */
export const isAtFirstPosition = (message, poll) => {
    if (message.pollMoved) return false;
    if (!poll?.createdAt) return true;
    return Math.abs(toMillis(message.timestamp) - toMillis(poll.createdAt)) <= FIRST_POSITION_WINDOW_MS;
};

/**
 * Date and time in the app-wide format, in the device time zone.
 *
 * @param {number} millis Epoch milliseconds.
 * @returns {string} YYYY/MM/DD HH:mm:ss, or an empty string.
 */
export const formatPollDateTime = millis => (millis ? moment(millis).format(DATE_TIME_FORMAT) : '');

/**
 * Tells whether voting has ended, by status or by a deadline already passed.
 *
 * @param {object} poll Poll view model.
 * @param {number} now Current epoch milliseconds.
 * @returns {boolean} True once the poll is closed.
 */
export const isPollClosed = (poll, now = Date.now()) =>
    poll?.status === POLL_STATUS.CLOSED || Boolean(poll?.closesAt && now >= poll.closesAt);

/**
 * Share of the voters who picked an option, as a whole percent rather than
 * the app's usual 2 decimals.
 *
 * @param {number} voteCount Votes on the option.
 * @param {number} totalVoters Participants who voted at least once.
 * @returns {number} 0-100.
 */
export const getVoteShare = (voteCount, totalVoters) =>
    totalVoters > 0 ? Math.round((Number(voteCount) / totalVoters) * 100) : 0;

/**
 * Options shown on the card: most voted first, ties in the creator's order.
 *
 * @param {object[]} options Poll options.
 * @returns {object[]} Up to POLL_CARD_OPTION_COUNT options.
 */
export const getCardOptions = (options = []) =>
    [...options].sort((a, b) => b.voteCount - a.voteCount || a.position - b.position).slice(0, POLL_CARD_OPTION_COUNT);

/**
 * Poll as the screens read it, from the server object or, before that loads, from the
 * snapshot the poll message carries. The snapshot has no votes and no status.
 *
 * @param {object} poll Server poll object, or null.
 * @param {object} snapshot `msgBody.poll` of the poll message.
 * @param {string} messageId Id of the poll message.
 * @returns {object} Poll view model.
 */
export const toPollView = (poll, snapshot = {}, messageId = '') => {
    if (poll) {
        return {
            ...poll,
            options: [...(poll.options || [])]
                .map(option => ({ ...option, voters: option.voters || [] }))
                .sort((a, b) => a.position - b.position),
            myOptionIds: poll.myOptionIds || [],
            isLoaded: true,
        };
    }
    return {
        pollId: snapshot.pollId || '',
        messageId,
        createdBy: snapshot.createdBy || '',
        question: snapshot.question || '',
        multipleAnswers: Boolean(snapshot.multipleAnswers),
        closesAt: toMillis(snapshot.closesAt) || null,
        status: POLL_STATUS.OPEN,
        totalVoters: 0,
        myOptionIds: [],
        options: (snapshot.options || []).map((option, position) => ({
            optionId: option.optionId || `${messageId}-${position}`,
            text: option.text || '',
            position,
            voteCount: 0,
            voters: [],
        })),
        isLoaded: false,
    };
};
